import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IPersonnelOnBoard } from '../../models/current-personnel/personnel-on-board.model';
import { IPersonnelWithMissingConfirmation } from '../../models/current-personnel/personnel-with-missing-confirmation.model';
import { CurrentPersonnelApiService } from '../../services/current-personnel-api.service';
import {
  ConfirmLeave,
  ConfirmLeaveMultipleUsers,
  ConfirmOnBoard,
  ConfirmOnBoardMultipleUsers,
  EditMultipleOnBoardPersonnel,
  LoadAllCurrentPersonnel,
  LoadAllMissingConfirmations,
  LoadCurrentPersonnel,
  LoadCurrentPersonnelForDepartment,
  LoadMissingConfirmations,
} from './current-personnel.actions';

export interface CurrentPersonnelStateModel {
  currentPersonnelOnBoard: IPersonnelOnBoard[];
  currentPersonnelOnBoardTotalPages: number;
  currentPersonnelOnBoardTotalItems: number;
  allCurrentPersonnel: IPersonnelOnBoard[];
  currentPersonnelByDepartmentId: Record<string, IPersonnelOnBoard[]>;
  missingConfirmations: IPersonnelWithMissingConfirmation[];
  missingConfirmationsTotalPages: number;
  missingConfirmationsTotalItems: number;
  allMissingConfirmations: IPersonnelWithMissingConfirmation[];
}

const CURRENT_PERSONNEL_STATE_TOKEN = new StateToken<CurrentPersonnelStateModel>(
  'CurrentPersonnel'
);

@State<CurrentPersonnelStateModel>({
  name: CURRENT_PERSONNEL_STATE_TOKEN,
  defaults: {
    currentPersonnelOnBoard: [],
    currentPersonnelOnBoardTotalPages: 0,
    currentPersonnelOnBoardTotalItems: 0,
    allCurrentPersonnel: [],
    currentPersonnelByDepartmentId: {},
    missingConfirmations: [],
    missingConfirmationsTotalPages: 0,
    missingConfirmationsTotalItems: 0,
    allMissingConfirmations: [],
  },
})
@Injectable()
export class CurrentPersonnelState {
  constructor(private service: CurrentPersonnelApiService) {}

  @Action(LoadCurrentPersonnel, { cancelUncompleted: true })
  loadCurrentPersonnel(
    { getState, setState }: StateContext<CurrentPersonnelStateModel>,
    { filter }: LoadCurrentPersonnel
  ) {
    return this.service.getAllCurrentPersonnelFromPagedUrl(filter).pipe(
      tap((response: ApiResponse<IPersonnelOnBoard[]>) => {
        const state = getState();
        setState({
          ...state,
          currentPersonnelOnBoard: response.data,
          currentPersonnelOnBoardTotalPages: response.totalPages,
          currentPersonnelOnBoardTotalItems: response.totalItems,
        });
      })
    );
  }

  @Action(LoadAllCurrentPersonnel, { cancelUncompleted: true })
  loadAllCurrentPersonnel({ getState, setState }: StateContext<CurrentPersonnelStateModel>) {
    return this.service.getAllCurrentPersonnel().pipe(
      tap((response: IPersonnelOnBoard[]) => {
        const state = getState();
        setState({
          ...state,
          allCurrentPersonnel: response,
        });
      })
    );
  }

  @Action(LoadCurrentPersonnelForDepartment)
  loadCurrentPersonnelForDepartment(
    { getState, patchState }: StateContext<CurrentPersonnelStateModel>,
    { departmentId }: LoadCurrentPersonnelForDepartment
  ) {
    return this.service.getAllCurrentPersonnel(departmentId).pipe(
      tap((response: IPersonnelOnBoard[]) => {
        patchState({
          currentPersonnelByDepartmentId: {
            ...getState().currentPersonnelByDepartmentId,
            [departmentId]: response,
          },
        });
      })
    );
  }

  @Action(LoadMissingConfirmations, { cancelUncompleted: true })
  loadMissingConfirmations(
    { getState, setState }: StateContext<CurrentPersonnelStateModel>,
    { filter }: LoadMissingConfirmations
  ) {
    return this.service.getAllMissingConfirmationsFromPagedUrl(filter).pipe(
      tap((response: ApiResponse<IPersonnelWithMissingConfirmation[]>) => {
        const state = getState();
        setState({
          ...state,
          missingConfirmations: response.data,
          missingConfirmationsTotalPages: response.totalPages,
          missingConfirmationsTotalItems: response.totalItems,
        });
      })
    );
  }

  @Action(LoadAllMissingConfirmations, { cancelUncompleted: true })
  loadAllMissingConfirmations({ getState, setState }: StateContext<CurrentPersonnelStateModel>) {
    return this.service.getAllMissingConfirmations().pipe(
      tap((response: IPersonnelWithMissingConfirmation[]) => {
        const state = getState();
        setState({
          ...state,
          allMissingConfirmations: response,
        });
      })
    );
  }

  @Action(ConfirmOnBoard, { cancelUncompleted: true })
  @OnSuccess({ message: 'confirmedOnBoard' })
  confirmOnBoard(ctx: StateContext<CurrentPersonnelStateModel>, { request }: ConfirmOnBoard) {
    return this.service.confirmOnBoard(request);
  }

  @Action(ConfirmOnBoardMultipleUsers, { cancelUncompleted: true })
  @OnSuccess({ message: 'confirmedOnBoard' })
  confirmOnBoardMultipleUsers(
    ctx: StateContext<CurrentPersonnelStateModel>,
    { request }: ConfirmOnBoardMultipleUsers
  ) {
    return this.service.confirmOnBoardMultipleUsers(request);
  }

  @Action(ConfirmLeave, { cancelUncompleted: true })
  @OnSuccess({ message: 'confirmedLeave' })
  confirmLeave(ctx: StateContext<CurrentPersonnelStateModel>, { request }: ConfirmLeave) {
    return this.service.confirmLeave(request);
  }

  @Action(ConfirmLeaveMultipleUsers, { cancelUncompleted: true })
  @OnSuccess({ message: 'confirmedLeaveMultipleUsers' })
  confirmLeaveMultipleUsers(
    ctx: StateContext<CurrentPersonnelStateModel>,
    { request }: ConfirmLeaveMultipleUsers
  ) {
    return this.service.confirmLeaveMultipleUsers(request);
  }

  @Action(EditMultipleOnBoardPersonnel, { cancelUncompleted: true })
  @OnSuccess({ message: 'editedMultipleCurrentPersonnel' })
  editMultipleCurrentPersonnel(
    ctx: StateContext<CurrentPersonnelStateModel>,
    { request }: EditMultipleOnBoardPersonnel
  ) {
    return this.service.editMultipleCurrentPersonnel(request);
  }

  @Selector()
  static getCurrentPersonnel(state: CurrentPersonnelStateModel) {
    return state.currentPersonnelOnBoard;
  }

  @Selector()
  static getCurrentPersonnelTotalPages(state: CurrentPersonnelStateModel) {
    return state.currentPersonnelOnBoardTotalPages;
  }

  @Selector()
  static getCurrentPersonnelTotalItems(state: CurrentPersonnelStateModel) {
    return state.currentPersonnelOnBoardTotalItems;
  }

  @Selector()
  static getMissingConfirmations(state: CurrentPersonnelStateModel) {
    return state.missingConfirmations;
  }

  @Selector()
  static getMissingConfirmationsTotalPages(state: CurrentPersonnelStateModel) {
    return state.missingConfirmationsTotalPages;
  }

  @Selector()
  static getMissingConfirmationsTotalItems(state: CurrentPersonnelStateModel) {
    return state.missingConfirmationsTotalItems;
  }

  @Selector()
  static getAllCurrentPersonnel(state: CurrentPersonnelStateModel): IPersonnelOnBoard[] {
    return state.allCurrentPersonnel;
  }

  static getCurrentPersonnelForDepartment(
    departmentId: string
  ): (state: CurrentPersonnelStateModel) => IPersonnelOnBoard[] {
    return createSelector([CurrentPersonnelState], (state: CurrentPersonnelStateModel) => {
      return state.currentPersonnelByDepartmentId[departmentId] || [];
    });
  }

  @Selector()
  static getAllMissingConfirmations(
    state: CurrentPersonnelStateModel
  ): IPersonnelWithMissingConfirmation[] {
    return state.allMissingConfirmations;
  }
}
